import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import Quill from 'quill';

import 'quill/dist/quill.snow.css';

const DEFAULT_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
    [{ color: [] }, { background: [] }, { align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }, { font: [] }],
    ['link', 'image', 'video'],
    ['clean']
  ],
  clipboard: { matchVisual: false }
};

const DEFAULT_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'video',
  'color',
  'background',
  'code-block',
  'align'
];

const normalizeEditorValue = value => {
  const nextValue = String(value || '').trim();
  return nextValue === '<p><br></p>' ? '' : nextValue;
};

const setEditorHtml = (editor, value) => {
  const normalizedValue = normalizeEditorValue(value);
  const currentValue = normalizeEditorValue(editor.root.innerHTML);

  if (normalizedValue === currentValue) {
    return;
  }

  const selection = editor.getSelection();

  editor.setText('', 'silent');

  if (normalizedValue) {
    editor.clipboard.dangerouslyPasteHTML(0, normalizedValue, 'silent');
  }

  if (selection) {
    const maxIndex = Math.max(editor.getLength() - 1, 0);
    editor.setSelection(Math.min(selection.index, maxIndex), selection.length, 'silent');
  }
};

const QuillEditor = forwardRef(
  (
    {
      value,
      onChange,
      modules,
      formats,
      readOnly,
      placeholder,
      className,
      style,
      theme
    },
    ref
  ) => {
    const editorContainerRef = useRef(null);
    const editorRef = useRef(null);
    const changeHandlerRef = useRef(onChange);
    const syncingRef = useRef(false);
    const valueRef = useRef(value);

    changeHandlerRef.current = onChange;
    valueRef.current = value;

    useImperativeHandle(
      ref,
      () => ({
        getEditor: () => editorRef.current,
        focus: () => editorRef.current?.focus?.(),
        blur: () => editorRef.current?.blur?.(),
        getModule: name => editorRef.current?.getModule?.(name),
        root: editorRef.current?.root || null
      }),
      []
    );

    useEffect(() => {
      const editorContainer = editorContainerRef.current;

      if (!editorContainer) {
        return undefined;
      }

      const editor = new Quill(editorContainer, {
        theme,
        modules,
        formats,
        placeholder,
        readOnly: false
      });

      editorRef.current = editor;
      setEditorHtml(editor, valueRef.current);

      const handleTextChange = (_delta, _oldDelta, source) => {
        if (syncingRef.current || source === 'silent') {
          return;
        }

        changeHandlerRef.current(normalizeEditorValue(editor.root.innerHTML));
      };

      editor.on('text-change', handleTextChange);

      return () => {
        editor.off('text-change', handleTextChange);
        editorRef.current = null;
        editorContainer.innerHTML = '';
      };
    }, [formats, modules, placeholder, theme]);

    useEffect(() => {
      const editor = editorRef.current;

      if (!editor) {
        return;
      }

      syncingRef.current = true;
      setEditorHtml(editor, value);
      syncingRef.current = false;
    }, [value]);

    useEffect(() => {
      const editor = editorRef.current;

      if (!editor) {
        return;
      }

      editor.enable(!readOnly);
    }, [readOnly]);

    return (
      <div className={className} style={style}>
        <div ref={editorContainerRef} />
      </div>
    );
  }
);

QuillEditor.displayName = 'QuillEditor';

QuillEditor.propTypes = {
  className: PropTypes.string,
  formats: PropTypes.arrayOf(PropTypes.string),
  modules: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  style: PropTypes.object,
  theme: PropTypes.string,
  value: PropTypes.string.isRequired
};

QuillEditor.defaultProps = {
  className: '',
  formats: DEFAULT_FORMATS,
  modules: DEFAULT_MODULES,
  placeholder: '',
  readOnly: false,
  style: undefined,
  theme: 'snow'
};

export default QuillEditor;
